import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, authApi } from '../services/api';
import { AxiosResponse } from 'axios';
// Define User type directly here if not exported from a module
export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  hourly_rate: number;
  is_admin: boolean;
  // Add realm_access if needed based on callback data structure
  realm_access?: {
      roles: string[];
  };
  employee?: any; // Add employee if it's part of the User structure used in context
}
// Remove potentially problematic import if User is defined here
// import { User } from '../types'; 

// Token refresh constants
const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes in milliseconds
const TOKEN_EXPIRY_WINDOW = 2 * 60 * 1000; // 2 minutes before expiry to refresh
const TOKEN_MAX_AGE = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

let tokenRefreshFailCount = 0;
const MAX_TOKEN_REFRESH_FAILS = 3;
let isRefreshing = false; // Flag to prevent concurrent refreshes

// Define the callback response interface
interface AuthCallbackResponse {
  access_token: string;
  refresh_token?: string; // Expect refresh token from callback
  user: User; // Use imported User type
  status: string;
  token_type?: string;
  expires_in?: number;
}

// Define the refresh response interface
interface RefreshResponse {
  access_token: string;
  refresh_token?: string; // Expect potentially new refresh token
  token_type: string;
  expires_in: number;
  status: string;
}

// Helper function for user data
const clearUserData = () => {
  localStorage.removeItem('user');
};
// -------------------------------------------

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  switchTestUser: (userId: number) => Promise<void>;
  isTestMode: boolean;
  directLogin: (userId: number) => Promise<void>;
  handleAuthCallback: () => Promise<void>;
  isAdmin: boolean;
  refreshToken: () => Promise<boolean>; // Expose refreshToken for interceptor
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isTestMode, setIsTestMode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Refactored refreshToken --- 
  const refreshToken = async (): Promise<boolean> => {
    if (isRefreshing) {
      console.log('AuthProvider: Token refresh already in progress.');
      return false; 
    }

    if (isTestMode) {
      console.log('AuthProvider: In test mode, not refreshing token.');
      return false;
    }
    
    isRefreshing = true;
    console.log('AuthProvider: Attempting token refresh...');

    try {
      // Call the backend refresh endpoint - cookies will be sent automatically
      const response = await authApi.post<RefreshResponse>('/auth/refresh');
      
      console.log('AuthProvider: Token refreshed successfully.');
      
      // No need to store tokens - cookies are handled by the browser
      
      tokenRefreshFailCount = 0;
      isRefreshing = false;
      return true;

    } catch (error: any) {
      console.error('AuthProvider: Refresh token error:', error);
      tokenRefreshFailCount++;
      isRefreshing = false;

      if (error.response && error.response.status === 401) {
        console.log('AuthProvider: Refresh failed (401), likely invalid refresh token. Forcing logout.');
        clearUserData();
        setUser(null);
      } else if (tokenRefreshFailCount >= MAX_TOKEN_REFRESH_FAILS) {
        console.log(`AuthProvider: Max refresh retries (${MAX_TOKEN_REFRESH_FAILS}) reached. Forcing logout.`);
        clearUserData();
        setUser(null);
      }
      return false;
    }
  };
  // -------------------------------

  // --- useEffect for initial load --- (REVISED)
  useEffect(() => {
    const verifySession = async () => {
      console.log('AuthProvider: Initial load check - verifying session via API...');
      setLoading(true); // Ensure loading state while checking
      try {
        // Attempt to call the /me endpoint. 
        // The Axios interceptor in services/api.ts should handle token refresh if needed.
        // The cookie will be sent automatically by the browser.
        const response = await api.get<User>('/employees/me'); // Use User type here
        
        if (response.data) {
          console.log('AuthProvider: Session verified via API. User:', response.data);
          setUser(response.data);
          // Optionally store in localStorage if needed elsewhere, but it's not the primary source now
          localStorage.setItem('user', JSON.stringify(response.data)); 
        } else {
           // Should not happen if API returns valid user or 401/403 handled by interceptor
           console.warn('AuthProvider: /me endpoint returned success but no user data?');
           clearUserData();
           setUser(null);
        }
      } catch (error: any) {
        // Axios interceptor might handle 401/403 by refreshing or logging out.
        // If error still reaches here, it means session is invalid.
        console.log('AuthProvider: Session verification failed (likely no valid cookie/session). Error:', error.message);
        clearUserData();
        setUser(null);
      }
      setLoading(false);
    };

    verifySession();
    
    // We don't need dependencies here as it only runs once on mount
  }, []);
 // -------------------------------
 
 // --- useEffect for refresh interval ---
  useEffect(() => {
    if (user && !isTestMode) { // Only run if logged in and not test mode
      console.log('AuthProvider: Setting up token refresh interval.');
      // Initial check shortly after login/load
      const initialCheck = setTimeout(() => refreshToken(), 5000); // e.g., 5 seconds after load
      const intervalId = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
      
      // Optional: Refresh on tab focus
      // const handleVisibilityChange = () => {...
      // document.addEventListener('visibilitychange', handleVisibilityChange); 
      
      return () => {
        console.log('AuthProvider: Clearing token refresh interval.');
        clearTimeout(initialCheck);
        clearInterval(intervalId);
        // document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user, isTestMode]); // Rerun if user logs in/out or test mode changes
 // --------------------------------

 // --- handleAuthCallback --- (Store refresh token)
 const handleAuthCallback = async () => {
    try {
      console.log('✅ AuthProvider: Handling auth callback...');
      setLoading(true);
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state'); 

      if (!code) throw new Error('No authorization code found');
      console.log(`✅ AuthProvider: Auth code found: ${code.substring(0, 10)}...`);

      // Define retry function (keep existing logic, ensure it uses correct URL)
      const exchangeTokenWithRetry = async (retries = 3, timeout = 15000): Promise<AxiosResponse<AuthCallbackResponse>> => {
           try {
             console.log(`✅ Exchanging code (retries left: ${retries})`);
             // Path should be relative to authApi baseURL
             const requestUrl = `/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state || '')}`;
             const response = await authApi.get<AuthCallbackResponse>(requestUrl, {
                timeout: timeout,
                headers: { /* cache control headers */ }
             });
             return response;
           } catch (error: any) {
              if (retries <= 0) throw error;
              console.log(`✅ Exchange failed, retrying...`);
              await new Promise(r => setTimeout(r, 1000));
              return exchangeTokenWithRetry(retries - 1, Math.min(timeout * 1.5, 30000));
           }
       };

      const response = await exchangeTokenWithRetry();
      console.log('✅ AuthProvider: Token exchange successful!');

      // Only need to get user data - tokens are handled as cookies
      const { user: userData } = response.data;

      if (!userData) {
         throw new Error('Invalid response from server');
      }

      // Store user data
      localStorage.setItem('user', JSON.stringify(userData));
      console.log('✅ AuthProvider: Saved user data.');

      // Update state
      setUser(userData);
      setIsTestMode(false); // Successful callback means not test mode

      // Clean up URL and redirect
      const redirectPath = sessionStorage.getItem('auth_redirect_path') || '/myshifts';
      window.history.replaceState({}, document.title, redirectPath);
      sessionStorage.removeItem('auth_redirect_path');
      console.log('✅ AuthProvider: Callback complete, redirecting to:', redirectPath);
      // Navigation might happen implicitly due to state change + ProtectedRoute

      setLoading(false);

    } catch (error) {
      console.error('✅ AUTH ERROR during callback handling:', error);
      clearUserData();
      setUser(null);
      setError('Authentication callback failed. Please try logging in again.');
      setLoading(false);
      window.history.replaceState({}, document.title, '/login');
    }
  };
  // ------------------------

  // --- logout --- (Clear refresh token)
  const logout = () => {
    console.log('AuthProvider: Logging out...');
    
    // Clear user data from storage and state
    clearUserData();
    setUser(null);
    setIsTestMode(false);
    
    // Redirect to logout endpoint which will clear the cookies
    const backendLogoutUrl = `/api/v1/auth/logout`;
    window.location.href = backendLogoutUrl;
  };
  // --------------

  // ... (fetchUserById, switchTestUser, login remain largely the same) ...
  const login = async () => {
    // Log entry into the function
    console.log("AuthProvider: login function ENTERED."); 
    try {
      console.log('AuthProvider: Initiating login...');
      setLoading(true);
      setError(null);
      
      console.log("AuthProvider: Clearing user data...");
      localStorage.removeItem('user');
      console.log("AuthProvider: Storage cleared.");
      
      const currentPath = window.location.pathname;
      console.log("AuthProvider: Storing redirect path:", currentPath);
      sessionStorage.setItem('auth_redirect_path', currentPath);
      
      const nonce = Math.random().toString(36).substring(2, 15);
      console.log("AuthProvider: Generated nonce:", nonce);
      
      let retries = 3;
      let success = false;
      let login_url = '';
      
      console.log("AuthProvider: Entering retry loop to get login URL...");
      while (retries > 0 && !success) {
        try {
          console.log(`AuthProvider: Requesting login URL (retries left: ${retries})`);
          // Path is relative to authApi baseURL
          const requestPath = `/auth/login-url?nonce=${nonce}`; 
          console.log("AuthProvider: Calling authApi.get with path:", requestPath);
          const response = await authApi.get(requestPath);
          console.log("AuthProvider: authApi.get response received:", response);
          login_url = response.data.login_url;
          console.log("AuthProvider: Extracted login_url:", login_url);
          success = true;
        } catch (e) {
          console.error(`AuthProvider: Error getting login URL (retry ${3-retries+1}):`, e);
          retries--;
          if (retries <= 0) {
            console.error("AuthProvider: Max retries reached for login URL.");
            throw e; // Re-throw after max retries
          }
          await new Promise(r => setTimeout(r, 1000));
        }
      }
      
      console.log('AuthProvider: Login URL obtained. Redirecting browser to:', login_url);
      // Ensure login_url is valid before redirecting
      if (login_url && typeof login_url === 'string') {
          window.location.href = login_url;
      } else {
          console.error('AuthProvider: Invalid login_url received, cannot redirect.', login_url);
          throw new Error("Invalid login URL received from backend.");
      }

    } catch (error) {
      console.error('AuthProvider: Error in login function:', error);
      setError('Unable to initiate login. Please check console for details.');
      setLoading(false); // Ensure loading is stopped on error
    }
  };

  const fetchUserById = async (userId: number) => { /* ... existing logic ... */ };
  const switchTestUser = async (userId: number): Promise<void> => { /* ... existing logic ... */ };
  const directLogin = async (userId: number): Promise<void> => { 
    console.log("Direct login with user ID:", userId);
    
    // Clear existing auth data
    localStorage.removeItem('authData');
    api.defaults.headers.common['Authorization'] = '';
    
    try {
      // Fetch user data
      const userResponse = await api.get(`/api/users/${userId}`);
      if (!userResponse.data) {
        throw new Error('Failed to fetch user data');
      }
      
      const userData = userResponse.data;
      
      // Get token (use existing token if available, or create placeholder)
      let token;
      try {
        const tokenResponse = await api.post('/api/auth/admin-token', { user_id: userId });
        token = tokenResponse.data.token;
      } catch (error) {
        console.error('Error getting admin token:', error);
        // Use placeholder if token retrieval fails
        token = `test_token_${userId}_${Date.now()}`;
      }
      
      // Save auth data
      const authData = {
        access_token: token,
        user: userData
      };
      
      localStorage.setItem('authData', JSON.stringify(authData));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Update state
      setUser(userData);
      setIsTestMode(true);
      setLoading(false);
      
    } catch (error) {
      console.error('Direct login error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    switchTestUser,
    isTestMode,
    directLogin,
    handleAuthCallback,
    isAdmin: user?.is_admin || false,
    refreshToken // Expose refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children} {/* Optionally render children only when loading is false */}
      {/* Or show a global loading indicator based on 'loading' state */}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};